import { useTranslation } from 'react-i18next'
import { useRouteAccess } from '@omnea-app/fe-authz/useRouteAccess'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { useVisibleCatalogueSettings } from '@omnea-app/fe-settings/catalogues/useVisibleCatalogueSettings'
import {
  ArchiveOMIcon,
  AutoAwesomeOMIcon,
  BellOMIcon,
  BoxCheckOMIcon,
  CartOMIcon,
  CheckboxListOMIcon,
  ClipboardCheckOMIcon,
  CodeOMIcon,
  CompressOMIcon,
  DownloadSquareOMIcon,
  FileCheckOMIcon,
  FileOMIcon,
  FlagOMIcon,
  FolderOMIcon,
  GavelOMIcon,
  GridLayoutOMIcon,
  GridOMIcon,
  InvoiceOMIcon,
  KeyOMIcon,
  LanguageOMIcon,
  LockedPadlockOMIcon,
  PeopleGroupOMIcon,
  PersonOMIcon,
  SettingsOMIcon,
  ShieldCheckOMIcon,
  SitemapOMIcon,
  SlidersOMIcon,
  SparkOMIcon,
  SyncOMIcon,
} from '@omnea-app/icons'
import { useIntegrationsItems } from '../hooks/useIntegrationsItems'
import type { SidebarSection } from '@omnea-app/fe-app-shell/sidebar/sectionItem'

/** The settings menu's sections, before route-access filtering: both shells' settings navs render these. */
export const useSettingsSections = (): SidebarSection[] => {
  const { t: tCommon } = useTranslation('common')
  const featureFlags = useFeatureFlags()
  const routeAccess = useRouteAccess()
  const integrationsItems = useIntegrationsItems(routeAccess.get('settingsIntegrations') === 'allowed')
  const hasVisibleCatalogueSettings = useVisibleCatalogueSettings().length > 0

  const personalSettingSection: SidebarSection = {
    title: tCommon('sidebar.account-title'),
    items: [
      {
        label: tCommon('sidebar.notifications'),
        icon: <BellOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsNotifications' },
      },
      {
        label: tCommon('sidebar.preferences'),
        icon: <SlidersOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsPreferences' },
      },
    ],
  }

  const organisationSection: SidebarSection = {
    title: tCommon('sidebar.organisation-title'),
    items: [
      {
        label: tCommon('sidebar.default-notifications'),
        icon: <BellOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsDefaultNotifications' },
      },
      { label: tCommon('sidebar.ai'), icon: <SparkOMIcon />, destination: { kind: 'route', routeKey: 'settingsAi' } },
      {
        label: tCommon('sidebar.security'),
        icon: <LockedPadlockOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsSecurity' },
      },
      {
        label: tCommon('sidebar.core-data'),
        icon: <GridLayoutOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsCoreData' },
      },
      {
        label: tCommon('sidebar.users-and-groups'),
        icon: <PeopleGroupOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsUsersAndGroups' },
      },
      {
        label: tCommon('sidebar.permissions'),
        icon: <KeyOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsPermissions' },
      },
      {
        label: tCommon('sidebar.user-properties'),
        icon: <PersonOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsUserProperties' },
      },
      {
        label: tCommon('sidebar.api'),
        icon: <CodeOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsApi' },
      },
      {
        label: tCommon('sidebar.import'),
        icon: <DownloadSquareOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsImport' },
      },
      {
        label: tCommon('sidebar.audit-log'),
        icon: <CheckboxListOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsAuditLog' },
      },
      {
        label: tCommon('sidebar.external-archive'),
        icon: <ArchiveOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsExternalArchive' },
        showsOmneanOnlyBadge: true,
      },
    ],
  }

  const featuresSection: SidebarSection = {
    title: tCommon('sidebar.features-title'),
    items: [
      {
        label: tCommon('sidebar.agent-library'),
        icon: <AutoAwesomeOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsAgentLibrary' },
      },
      {
        label: tCommon('sidebar.workflows'),
        icon: <SitemapOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsWorkflows' },
      },
      {
        label: tCommon('sidebar.budget-approvals'),
        icon: <PersonOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsDynamicAssignees' },
      },
      {
        label: tCommon('sidebar.forms'),
        icon: <FileOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsForms' },
      },
      {
        label: tCommon('sidebar.sourcing'),
        icon: <CompressOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsSourcing' },
      },
      {
        label: tCommon('sidebar.auctions'),
        icon: <GavelOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsAuctions' },
      },
      {
        label: tCommon('sidebar.risk-management'),
        icon: <ShieldCheckOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsRisks' },
      },
      // Which catalogue kinds an organisation has is data, not a route rule, so it stays an item condition.
      ...(hasVisibleCatalogueSettings
        ? [
            {
              label: tCommon('sidebar.catalogues'),
              icon: <CartOMIcon />,
              destination: { kind: 'route', routeKey: 'settingsCatalogues' } as const,
            },
          ]
        : []),
      {
        label: tCommon('sidebar.product-supplier-categories'),
        icon: <GridOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsCategories' },
      },
      {
        label: tCommon('sidebar.document-review-playbooks'),
        icon: <FolderOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsDocumentStorage' },
      },
      {
        label: tCommon('sidebar.policies'),
        icon: <FileCheckOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsPolicies' },
      },
      {
        label: tCommon('sidebar.environment-sync'),
        icon: <SyncOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsEnvironmentSync' },
      },
      {
        label: tCommon('sidebar.feature-flags', 'Feature flags'),
        icon: <FlagOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsInternalFeatureFlags' },
        showsOmneanOnlyBadge: true,
      },
      {
        label: tCommon('sidebar.localisation'),
        icon: <LanguageOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsLocalisation' },
      },
      {
        label: tCommon('sidebar.admin-settings'),
        icon: <SettingsOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsAdmin' },
      },
      // The migration tool exists to migrate organisations onto agreements, so the flag hides the item
      // for those already on them rather than gating the route.
      ...(featureFlags.CREATE_AGREEMENT
        ? [
            {
              label: tCommon('sidebar.agreements-migration'),
              icon: <SettingsOMIcon />,
              destination: { kind: 'route', routeKey: 'settingsAgreementsMigration' } as const,
              showsOmneanOnlyBadge: true,
            },
          ]
        : []),
    ],
  }

  const accountsPayableSection: SidebarSection = {
    title: tCommon('sidebar.accounts-payable-title'),
    items: [
      {
        label: tCommon('sidebar.goods-receipting'),
        icon: <BoxCheckOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsGoodsReceipting' },
      },
      {
        label: tCommon('sidebar.purchase-orders'),
        icon: <ClipboardCheckOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsPurchaseOrders' },
      },
      {
        label: tCommon('sidebar.invoice-processing'),
        icon: <InvoiceOMIcon />,
        destination: { kind: 'route', routeKey: 'settingsInvoiceProcessing' },
      },
    ],
  }

  const integrationsSection: SidebarSection = {
    title: tCommon('sidebar.integrations'),
    items: integrationsItems,
  }

  return [personalSettingSection, organisationSection, featuresSection, accountsPayableSection, integrationsSection]
}
